/**
 * SUB-1.47 · F-106 · normalising the person embedded on a ticket or comment.
 *
 * PostgREST returns a to-one embed as an OBJECT or as a single-element ARRAY
 * depending on whether it can prove the relationship is to-one. Across two
 * foreign keys and three call sites, `ticketNotificationService.ts` handled that
 * ambiguity by hand six times, in this shape:
 *
 *     const creatorName = Array.isArray(ticket?.user_profiles)
 *       ? (ticket?.user_profiles[0] && (ticket?.user_profiles[0] as { name?: string })?.name)
 *       : (ticket?.user_profiles && (ticket?.user_profiles as { name?: string })?.name) || 'Unknown';
 *
 * ⚠️ AND THAT EXPRESSION HAS A REAL BUG, not merely a readability problem. The
 * `|| 'Unknown'` binds to the FALSE branch of the ternary only. So the array
 * branch returns `undefined` — not `'Unknown'` — whenever the embed comes back
 * as an array whose first element has no name. One of the two shapes PostgREST
 * is free to choose silently produces `undefined` in an email subject line.
 *
 * Normalising once, in a tested function, is what stops that being rewritten a
 * seventh time.
 */

import { getUserDisplayName } from '@/utils/userUtils';
import type { TicketPerson } from '@/types/ticket';

/** The embed as PostgREST may return it: an object, a one-element array, or absent. */
export type EmbeddedPerson = TicketPerson | TicketPerson[] | null | undefined;

/**
 * The single person an embed refers to, or null.
 *
 * ⚠️ An empty array is null, not an empty person. A person with no readable
 * fields is a person the caller must be able to distinguish from an absent one.
 */
export function embeddedPerson(value: EmbeddedPerson): TicketPerson | null {
  if (!value) return null;
  if (Array.isArray(value)) return value.length > 0 ? (value[0] ?? null) : null;
  return value;
}

/**
 * What to render for an embedded person.
 *
 * `getUserDisplayName` stays the ONE definition of how a person is named — the
 * `[Inactive]` suffix and the `deleted+` email rule included. This function only
 * decides what happens when there is nobody to name.
 *
 * ⚠️ The fallback applies to BOTH embed shapes, which is the bug described in the
 * module header.
 */
export function embeddedPersonName(
  value: EmbeddedPerson,
  fallback = 'Unknown',
): string {
  const person = embeddedPerson(value);
  if (!person) return fallback;

  const name = getUserDisplayName(person).trim();

  // `getUserDisplayName` returns "Unknown User" when it holds a record with
  // nothing nameable in it. That is not a name; defer to the caller's fallback
  // so a ticket email says "Unknown" rather than "Unknown User".
  if (name === '' || name === 'Unknown User') return fallback;

  return name;
}
